import { ajvResolver } from '@hookform/resolvers/ajv'
import { ArticleOutlined, DeleteOutlined } from '@mui/icons-material'
import UploadIcon from '@mui/icons-material/Upload'
import { Box, Button, OutlinedInput } from '@mui/material'
import AuthInvalid from 'components/auth-invalid'
import { useAlert } from 'contexts/alert'
import { useAuth } from 'contexts/auth'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import CadastralPositionSchema, { ICadastralPositionFileFormData } from 'schema/automation/cadastral-position'
import api from 'services/api'
import { IProjectResult } from '../project/types'
import { useStyles } from './styles'

const CadastralPosition = () => {
  const [result, setResult] = useState<IProjectResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  const { isAuthenticated } = useAuth()
  const { openAlert } = useAlert()

  const params: any = useParams()
  const projectId = params.id

  const classes = useStyles()
  const history = useHistory()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { control, handleSubmit } = useForm<ICadastralPositionFileFormData>({
    resolver: ajvResolver(CadastralPositionSchema),
    defaultValues: {
      fileForm: [],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fileForm',
  })

  const handleFileInputClick = () => {
    fileInputRef.current?.click()
  }

  const handleAddFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const filesFromForm = e.target.files
      if (filesFromForm) {
        const fileListFromForm: File[] = [...filesFromForm]

        fileListFromForm.forEach((file) =>
          append({
            lineHeader: '1',
            lineHeaderSize: '1',
            primaryColumn: '',
          })
        )
        setFiles((prevFiles) => [...prevFiles, ...fileListFromForm])
      }
    },
    [append]
  )

  const handleRemoveFromList = useCallback(
    (index: number) => {
      if (isLoading) return

      const newFiles = files.filter((_, i) => i !== index)
      remove(index)
      setFiles(newFiles)
    },
    [files, isLoading, remove]
  )

  const handleFileSelect = useCallback(
    (e: any) => {
      e.preventDefault()
      const selectedFiles: File[] = [...e.dataTransfer.files]

      selectedFiles.forEach((file) =>
        append({
          lineHeader: '1',
          lineHeaderSize: '1',
          primaryColumn: '',
        })
      )
      setFiles((prevFiles) => [...prevFiles, ...selectedFiles])
    },
    [append]
  )

  const handleDragOver = useCallback((e: any) => e.preventDefault(), [])

  const handleSubmitFile: SubmitHandler<ICadastralPositionFileFormData> = useCallback(
    async (data) => {
      if (files.length === 0) {
        openAlert('Nenhum arquivo selecionado', 'error')
        return
      }

      try {
        setIsLoading(true)
        const formData = new FormData()

        const fileFormData: any = []

        // formData.append('filesType', 'posicoes_cadastrais')
        // formData.append('fileForm', JSON.stringify({ fileFormData }))

        files.forEach((file, i) => {
          fileFormData.push({ fileName: file.name, ...data.fileForm[i] })
          formData.append('files', file)
        })

        formData.append('projectId', projectId || '')

        await api.post('/conducco-cdp-apps/bate-cadastral/posicao-cadastral', formData)

        openAlert('Arquivos enviados com sucesso', 'success')

        setIsLoading(false)
        setFiles([])

        // window.parent.postMessage(`/projects/${projectId}`, '*')
      } catch (error) {
        console.log(error)
        openAlert('Erro ao enviar arquivos', 'error')
        setIsLoading(false)
      }
    },
    [files, projectId, openAlert]
  )

  const loadInitialData = async () => {
    await api
      .get(`/projects/${projectId}`)
      .then((response) => setResult(response.data.data))
      .catch((error) => {
        openAlert('Token inválido', 'error')
        return error
      })
  }

  useEffect(() => {
    if (projectId && isAuthenticated) {
      loadInitialData()
    }
  }, [projectId, isAuthenticated])

  if (!isAuthenticated) {
    return <AuthInvalid />
  }

  return (
    <Box
      component={'form'}
      className={classes.bodyContainer}
      onSubmit={handleSubmit(handleSubmitFile)}
    >
      <Box
        className={classes.uploadArea}
        onClick={handleFileInputClick}
        onDrop={handleFileSelect}
        onDragOver={handleDragOver}
      >
        <input
          type='file'
          name='file'
          accept='.xls, .xlsx, .pdf'
          multiple
          hidden
          ref={fileInputRef}
          onChange={handleAddFile}
        />

        <UploadIcon />

        <span className={classes.uploadAreaTextSend}>Arrastar e soltar ou clique para procurar</span>

        <span className={classes.uploadAreaTextInfo}>Somente arquivos nos formatos xls e xlsx são aceitos</span>
      </Box>

      <Box className={classes.uploadFileBox}>
        <Box className={classes.fileBody}>
          {files.map((file, i) => (
            <Box
              key={i}
              className={classes.fileItem}
            >
              <Box className={classes.fileText}>
                <ArticleOutlined className={classes.fileItemArticleIcon} />
                <span>{file.name}</span>
                <DeleteOutlined
                  className={classes.fileItemDeleteIcon}
                  onClick={() => handleRemoveFromList(i)}
                />
              </Box>

              <Box className={classes.fileBoxRow}>
                <Box className={classes.fileBoxForm}>
                  <Controller
                    control={control}
                    name={`fileForm.${i}.lineHeader`}
                    defaultValue={fields[i]?.lineHeader || ''}
                    render={({ field }) => (
                      <OutlinedInput
                        type='number'
                        placeholder='Digite o numero da linha'
                        className={classes.inputFileItem}
                        {...field}
                      />
                    )}
                  />
                  <span>Linha onde o cabeçalho começa</span>
                </Box>

                <Box className={classes.fileBoxForm}>
                  <Controller
                    control={control}
                    name={`fileForm.${i}.lineHeaderSize`}
                    defaultValue={fields[i]?.lineHeaderSize || ''}
                    render={({ field }) => (
                      <OutlinedInput
                        type='number'
                        placeholder='Digite o numero de linhas'
                        className={classes.inputFileItem}
                        {...field}
                      />
                    )}
                  />
                  <span>Quantidade de linhas do cabeçalho</span>
                </Box>

                <Box className={classes.fileBoxForm}>
                  <Controller
                    control={control}
                    name={`fileForm.${i}.primaryColumn`}
                    defaultValue={fields[i]?.primaryColumn || ''}
                    render={({ field }) => (
                      <OutlinedInput
                        type='text'
                        placeholder='Digite o nome do campo'
                        className={classes.inputFileTextItem}
                        {...field}
                      />
                    )}
                  />
                  <span>Qual a Nome do campo CPF</span>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {files.length > 0 && (
        <Box className={classes.actions}>
          <Button
            type='button'
            variant='outlined'
            disabled={isLoading}
            className={classes.cancelButton}
            onClick={() => setFiles([])}
          >
            Cancelar
          </Button>
          <Button
            type='submit'
            variant='contained'
            size='small'
            disabled={isLoading}
            className={classes.submitButton}
          >
            Enviar
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default CadastralPosition
